import React, { useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Card from '@material-ui/core/Card';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Grid,
  CardContent,
  Fade,
  makeStyles
} from '@material-ui/core';
import { useNotify } from 'react-admin';
import FileUpload from './FileUpload';
import { CloudUpload, Sync, GetApp, Publish } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    minHeight: '100vh'
  },
  mainCard: {
    borderRadius: 16,
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
    padding: theme.spacing(2)
  },
  title: {
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: theme.spacing(1)
  },
  subtitle: {
    color: '#7f8c8d',
    fontSize: '0.95rem'
  },
  actionCard: {
    borderRadius: 12,
    padding: theme.spacing(3),
    textAlign: 'center',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
    }
  },
  downloadCard: {
    background: 'white',
    color: '#2c3e50',
    border: '2px solid #667eea'
  },
  uploadCard: {
    background: 'white',
    color: '#2c3e50',
    border: '2px solid #f5576c'
  },
  syncCard: {
    background: 'white',
    color: '#2c3e50',
    border: '2px solid #4facfe'
  },
  actionIcon: {
    fontSize: '2.5rem',
    marginBottom: theme.spacing(2)
  },
  actionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    fontSize: '1.1rem'
  },
  actionDescription: {
    fontSize: '0.9rem',
    opacity: 0.9,
    marginBottom: theme.spacing(2)
  },
  actionButton: {
    borderRadius: 25,
    padding: '8px 24px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.95rem',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    '&:hover': {
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)'
    }
  },
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999
  }
}));

const ManageView = ({
  syncBqToPgNames,
  download,
  uploadCsv,
  resourceName,
  title,
  subtitle
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const notify = useNotify();

  const handleExportCarsData = async () => {
    if (loading) return;
    try {
      setLoading(true);
      await download();
      setLoading(false);
      notify('הייצוא בוצע בהצלחה', { type: 'success' });
    } catch (error) {
      console.error('Error exporting cars data:', error);
      notify('שגיאה בייצוא', { type: 'error' });
      setLoading(false);
    }
  };

  const handleFileSelect = async (file) => {
    if (uploadLoading) return;

    try {
      setUploadLoading(true);
      await uploadCsv(file);
      setUploadLoading(false);

      notify('העלאת הקובץ בוצעה בהצלחה', { type: 'success' });
    } catch (error) {
      console.error('Error uploading CSV file:', error);
      notify('שגיאה בהעלאת הקובץ', { type: 'error' });
      setUploadLoading(false);
    }
  };

  const handleSyncCarsData = async () => {
    if (loading) return;
    try {
      setLoading(true);
      await syncBqToPgNames();
      setLoading(false);
      notify('הסנכרון בוצע בהצלחה', { type: 'success' });
    } catch (error) {
      console.error('Error syncing cars data:', error);
      notify('שגיאה בסנכרון', { type: 'error' });
      setLoading(false);
    }
  };

  return (
    <Box className={classes.root}>
      <Paper className={classes.mainCard}>
        <CardContent>
          <Fade in timeout={800}>
            <Box className={classes.header}>
              <Typography variant="h4" className={classes.title}>
                {title}
              </Typography>
              <Typography variant="body1" className={classes.subtitle}>
                {subtitle}
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={3} style={{ marginTop: 16 }}>
            <Grid item xs={12} md={4} style={{ display: 'flex' }}>
              <Fade in timeout={1000} style={{ width: '100%' }}>
                <Card
                  className={`${classes.actionCard} ${classes.downloadCard}`}
                  onClick={handleExportCarsData}
                >
                  <GetApp className={classes.actionIcon} />
                  <Typography className={classes.actionTitle}>
                    הורדת קובץ CSV
                  </Typography>
                  <Typography className={classes.actionDescription}>
                    ייצא את כל {resourceName} לקובץ CSV
                  </Typography>
                  <Button
                    variant="contained"
                    className={classes.actionButton}
                    disabled={loading}
                    style={{
                      background: '#667eea',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'הורד עכשיו'
                    )}
                  </Button>
                </Card>
              </Fade>
            </Grid>

            <Grid item xs={12} md={4} style={{ display: 'flex' }}>
              <Fade in timeout={1200} style={{ width: '100%' }}>
                <Card className={`${classes.actionCard} ${classes.uploadCard}`}>
                  <Publish className={classes.actionIcon} />
                  <Typography className={classes.actionTitle}>
                    העלאת קובץ CSV
                  </Typography>
                  <Typography className={classes.actionDescription}>
                    העלה קובץ CSV חדש לעדכון {resourceName}
                  </Typography>
                  <FileUpload
                    onFileSelect={handleFileSelect}
                    acceptedTypes={['.csv']}
                    buttonText={uploadLoading ? 'מעלה...' : 'העלה קובץ'}
                    buttonIcon={<CloudUpload />}
                    disabled={uploadLoading}
                    customStyle={{
                      background: '#f5576c',
                      color: 'white',
                      border: 'none',
                      borderRadius: 25,
                      padding: '8px 24px',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                </Card>
              </Fade>
            </Grid>

            <Grid item xs={12} md={4} style={{ display: 'flex' }}>
              <Fade in timeout={1400} style={{ width: '100%' }}>
                <Card
                  className={`${classes.actionCard} ${classes.syncCard}`}
                  onClick={handleSyncCarsData}
                >
                  <Sync className={classes.actionIcon} />
                  <Typography className={classes.actionTitle}>
                    סנכרון {resourceName}
                  </Typography>
                  <Typography className={classes.actionDescription}>
                    סנכרן את השמות עם המערכת המרכזית
                  </Typography>
                  <Button
                    variant="contained"
                    className={classes.actionButton}
                    disabled={loading}
                    style={{
                      background: '#4facfe',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'סנכרן עכשיו'
                    )}
                  </Button>
                </Card>
              </Fade>
            </Grid>
          </Grid>
        </CardContent>
      </Paper>

      {(loading || uploadLoading) && <LoadingOverlay />}
    </Box>
  );
};

const LoadingOverlay = () => {
  const classes = useStyles();
  return (
    <Box className={classes.loadingOverlay}>
      <Box textAlign="center">
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" style={{ marginTop: 16, color: '#2c3e50' }}>
          מעבד...
        </Typography>
      </Box>
    </Box>
  );
};

export default ManageView;
